import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { socket } from '../utils/socket';
import GameBoard from './GameBoard';
import { ValidCell } from '../types';
import SimpleScoreBlock from './SimpleScoreBlock';
import ScoreHistoryModal from './ScoreHistoryModal';

interface GameState {
  id: string;
  gameMasters: string[];
  teamA: string[];
  teamB: string[];
  round: number;
  chosenLocationsA: ValidCell[];
  chosenLocationsB: ValidCell[];
  currentSelections: {
    teamA?: { position: ValidCell | null; confirmed: boolean };
    teamB?: { position: ValidCell | null; confirmed: boolean };
  };
  scores: {
    teamA: number;
    teamB: number;
  };
  alignments: {
    teamA: number;
    teamB: number;
  };
  status: 'waiting' | 'playing' | 'finished';
  board: {
    [key in ValidCell]?: 'A' | 'B';
  };
  blockedPositions: ValidCell[];
  masterBlockedPositions: ValidCell[];
  competitorSharesMarket: boolean;
  financeMode: boolean;
  shopPriceMultipliers: { [key in ValidCell]?: number };
  roundScores?: {
    teamA: number;
    teamB: number;
  }[];
}

const GameMaster: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [positionConflict, setPositionConflict] = useState<string | null>(null);
  const [blockPositionInput, setBlockPositionInput] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [connectionTimeout, setConnectionTimeout] = useState(false);
  const hasJoined = useRef(false);

  useEffect(() => {
    if (!gameId) {
      navigate('/');
      return;
    }

    // Timeout de connexion (10 secondes)
    const timeoutId = setTimeout(() => {
      if (!gameState) {
        setConnectionTimeout(true);
      }
    }, 10000);

    const handleConnect = () => {
      setIsConnected(true);
      setError(null);
      setConnectionTimeout(false);
      clearTimeout(timeoutId);
      if (!hasJoined.current) {
        socket.emit('joinGame', { gameId, role: 'master' });
        hasJoined.current = true;
      }
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      setError('Disconnected from server');
      hasJoined.current = false;
    };

    const handleGameState = (state: GameState) => {
      setGameState(state);
      setError(null);
      setConnectionTimeout(false);
      clearTimeout(timeoutId);
    };

    const handleError = (errorMessage: string) => {
      setError(errorMessage);
      setConnectionTimeout(false);
      clearTimeout(timeoutId);
    };

    const handlePositionConflict = (data: { position: ValidCell; message: string }) => {
      setPositionConflict(`${t('gameMaster.positionConflict', { position: data.position })}`);
      setTimeout(() => setPositionConflict(null), 5000);
    };

    const handleCompetitorEntered = (data: { position: ValidCell; message: string }) => {
      // Game Master also sees when competitor enters
      setPositionConflict(`${t('competitorEntered', { position: data.position })}`);
      setTimeout(() => setPositionConflict(null), 5000);
    };

    const handlePositionBlockedByCompetitor = (data: { message: string }) => {
      setPositionConflict(data.message);
      setTimeout(() => setPositionConflict(null), 5000);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('gameState', handleGameState);
    socket.on('error', handleError);
    socket.on('positionConflict', handlePositionConflict);
    socket.on('competitorEntered', handleCompetitorEntered);
    socket.on('positionBlockedByCompetitor', handlePositionBlockedByCompetitor);

    // Connexion initiale seulement si pas déjà connecté
    if (!socket.connected) {
      socket.connect();
    } else if (!hasJoined.current) {
      socket.emit('joinGame', { gameId, role: 'master' });
      hasJoined.current = true;
    }

    return () => {
      clearTimeout(timeoutId);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('gameState', handleGameState);
      socket.off('error', handleError);
      socket.off('positionConflict', handlePositionConflict);
      socket.off('competitorEntered', handleCompetitorEntered);
      socket.off('positionBlockedByCompetitor', handlePositionBlockedByCompetitor);
    };
  }, [gameId, navigate, t]); // Supprimé isConnected des dépendances

  const handleValidateMove = () => {
    if (!gameState || !gameId) return;
    socket.emit('validateMove', { gameId });
  };

  const handleResetRound = () => {
    if (!gameState || !gameId) return;

    if (window.confirm(t('gameMaster.confirmResetRound'))) {
      socket.emit('resetRound', { gameId });
    }
  };

  const handleBlockPosition = () => {
    if (!gameState || !gameId || !blockPositionInput) return;

    const validPosition = blockPositionInput.toUpperCase();
    if (!/^[MKDLW][1-5]$/.test(validPosition)) {
      setError(t('errors.invalidPosition'));
      return;
    }

    // Check if position is already occupied
    if (gameState.chosenLocationsA.includes(validPosition as ValidCell) ||
      gameState.chosenLocationsB.includes(validPosition as ValidCell)) {
      setError(t('errors.positionTaken'));
      return;
    }

    // Check if already blocked by master
    if (gameState.masterBlockedPositions.includes(validPosition as ValidCell)) {
      setError(t('gameMaster.positionAlreadyBlocked'));
      return;
    }

    socket.emit('blockPosition', { gameId, position: validPosition });
    setBlockPositionInput('');
    setError(null);
  };

  const handleUnblockPosition = (position: ValidCell) => {
    if (!gameState || !gameId) return;
    socket.emit('unblockPosition', { gameId, position });
  };

  const handleToggleCompetitorSharesMarket = () => {
    if (!gameState || !gameId) return;
    socket.emit('toggleCompetitorSharesMarket', { gameId });
  };

  // État de chargement avec timeout et bouton de rechargement
  if (connectionTimeout) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
        <div className="text-center space-y-6 max-w-md">
          <h1 className="text-4xl font-bold text-red-500 mb-2">{t('gameMaster.title')}</h1>
          <div className="space-y-4">
            <div className="text-red-400 text-lg">
              ⚠️ {t('connectionTimeout', 'Connexion échouée')}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('connectionTimeoutMessage', 'Impossible de se connecter au serveur de jeu. Vérifiez votre connexion internet et réessayez.')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold shadow-lg transition-all duration-200 hover:shadow-xl"
            >
              🔄 {t('reload', 'Recharger')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold shadow-lg transition-all duration-200 hover:shadow-xl"
            >
              🏠 {t('backToHome', 'Retour à l\'accueil')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnected || !gameState) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold mb-4">{t('gameMaster.title')}</h1>
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <div className="text-left">
              <p className="text-xl font-semibold">
                {!isConnected ? t('connecting', 'Connexion...') : t('loading', 'Chargement...')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {!isConnected
                  ? t('connectingMessage', 'Connexion au serveur de jeu')
                  : t('loadingMessage', 'Chargement de la partie en cours')
                }
              </p>
            </div>
          </div>
          {!isConnected && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 max-w-sm">
              {t('connectionHelp', 'Si la connexion prend plus de 10 secondes, essayez de recharger la page.')}
            </p>
          )}
        </div>
      </div>
    );
  }

  const canValidate =
    gameState.currentSelections.teamA?.position &&
    gameState.currentSelections.teamB?.position &&
    gameState.currentSelections.teamA.confirmed &&
    gameState.currentSelections.teamB.confirmed;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-4 transition-colors">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500 dark:bg-red-600 text-white rounded-lg shadow-md">
            {error}
          </div>
        )}

        {positionConflict && (
          <div className="mb-4 p-4 bg-yellow-500 dark:bg-yellow-600 text-white rounded-lg shadow-md">
            {positionConflict}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <GameBoard
              chosenLocationsA={gameState.chosenLocationsA}
              chosenLocationsB={gameState.chosenLocationsB}
              currentSelections={{
                teamA: gameState.currentSelections.teamA?.position,
                teamB: gameState.currentSelections.teamB?.position
              }}
              blockedPositions={gameState.blockedPositions}
              masterBlockedPositions={gameState.masterBlockedPositions}
              competitorSharesMarket={gameState.competitorSharesMarket}
              board={gameState.board}
              financeMode={gameState.financeMode}
              shopPriceMultipliers={gameState.shopPriceMultipliers}
            />
          </div>

          <div className="space-y-2">
            {/* Unified Score Block */}
            <div style={{ position: 'relative' }}>
              <SimpleScoreBlock
                teamAScore={gameState.scores.teamA}
                teamBScore={gameState.scores.teamB}
                teamAAlignments={gameState.alignments.teamA}
                teamBAlignments={gameState.alignments.teamB}
                currentRound={gameState.round}
                maxRounds={12}
                financeMode={gameState.financeMode}
              />
              {/* History Button */}
              <button
                onClick={() => setShowHistoryModal(true)}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  background: 'rgba(59, 130, 246, 0.2)',
                  border: '1px solid rgba(59, 130, 246, 0.4)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  zIndex: 10,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={t('scores.history')}
              >
                📊
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold mb-3">{t('currentMoves')}</h2>
              <div className="space-y-3 mb-3">
                <div className="flex justify-between items-center text-sm">
                  <span>{t('teamA')}:</span>
                  <div className="flex items-center">
                    <span className="mr-2 font-mono">
                      {gameState.currentSelections.teamA?.position || '-'}
                    </span>
                    {gameState.currentSelections.teamA?.confirmed && (
                      <span className="text-green-500">✓</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span>{t('teamB')}:</span>
                  <div className="flex items-center">
                    <span className="mr-2 font-mono">
                      {gameState.currentSelections.teamB?.position || '-'}
                    </span>
                    {gameState.currentSelections.teamB?.confirmed && (
                      <span className="text-green-500">✓</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleValidateMove}
                  className={`
                    py-2 px-3 rounded-lg font-bold shadow-sm text-sm
                    transition-all duration-200
                    ${canValidate
                      ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50 text-gray-500 dark:text-gray-400'
                    }
                  `}
                  disabled={!canValidate}
                >
                  {t('validate')}
                </button>

                <button
                  onClick={handleResetRound}
                  className="py-2 px-3 rounded-lg font-bold bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white transition-all duration-200 shadow-sm text-sm"
                >
                  {t('reset')}
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-sm">{t('gameMaster.competitorSharesMarket')}</div>
                  <button
                    onClick={handleToggleCompetitorSharesMarket}
                    className={`
                      relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ml-2
                      ${gameState.competitorSharesMarket ? 'bg-purple-600 dark:bg-purple-500' : 'bg-gray-400 dark:bg-gray-600'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                        ${gameState.competitorSharesMarket ? 'translate-x-5' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={blockPositionInput}
                    onChange={(e) => setBlockPositionInput(e.target.value)}
                    placeholder={t('enterPosition')}
                    className="flex-1 p-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400 focus:outline-none text-sm"
                    maxLength={2}
                  />
                  <button
                    onClick={handleBlockPosition}
                    className="px-3 py-2 rounded-lg font-bold bg-purple-500 hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700 text-white transition-all duration-200 shadow-sm text-sm"
                  >
                    {t('gameMaster.block')}
                  </button>
                </div>

                {gameState.masterBlockedPositions && gameState.masterBlockedPositions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase">{t('gameMaster.blockedPositions')}:</p>
                    <div className="flex flex-wrap gap-2">
                      {gameState.masterBlockedPositions.map((pos) => (
                        <div
                          key={pos}
                          className="flex items-center gap-1 bg-purple-100 dark:bg-purple-900/50 px-2 py-0.5 rounded border border-purple-200 dark:border-purple-800"
                        >
                          <span className="text-xs font-bold text-purple-800 dark:text-purple-200">{pos}</span>
                          <button
                            onClick={() => handleUnblockPosition(pos)}
                            className="text-purple-400 hover:text-red-500 text-xs font-bold px-1"
                            title={t('gameMaster.unblock')}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ScoreHistoryModal
            isOpen={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
            rounds={gameState.roundScores || []}
            currentRound={gameState.round}
          />
        </div>
      </div>
    </div>
  );
};

export default GameMaster;
